import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useEffect, useRef, useState } from 'react';
import { 
  ChevronRight, 
  Database, 
  SlidersHorizontal,
  Bot,
  UserCheck,
  Users,
  Settings,
  Eye,
  Scale,
  ArrowRight,
  ExternalLink
} from 'lucide-react';

// Animated counter component
const AnimatedCounter = ({ end, suffix = '', prefix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;
    
    const duration = 1500;
    const steps = 60;
    const stepDuration = duration / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += 1;
      const progress = current / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.round(end * easeOut));
      
      if (current >= steps) {
        clearInterval(timer);
        setCount(end);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [isVisible, end]);

  return (
    <span ref={ref} className="stat-number">
      {prefix}{count}{suffix}
    </span>
  );
};

// Conviction tile component
const ConvictionTile = ({ icon: Icon, title, body }) => {
  return (
    <Card className="bg-white border border-[#D9E2F0] rounded-xl shadow-lg hover:shadow-xl hover:border-[#C7D5E8] transition-all">
      <CardContent className="p-8">
        <div className="p-3 bg-[#1A56DB]/10 rounded-lg w-fit mb-4">
          <Icon className="text-[#1A56DB]" size={28} />
        </div>
        <h3 className="text-xl font-bold text-[#0A1628] mb-3">{title}</h3>
        <p className="text-[#6B7280] text-sm leading-relaxed">{body}</p>
      </CardContent>
    </Card>
  );
};

// Solution card component
const SolutionCard = ({ tag, tagColor, name, problem, solution, link, external = false }) => {
  const colorMap = {
    teal: 'bg-[#0E7490]/10 text-[#0E7490]',
    purple: 'bg-[#4F46E5]/10 text-[#4F46E5]',
    blue: 'bg-[#1A56DB]/10 text-[#1A56DB]',
    amber: 'bg-[#B45309]/10 text-[#B45309]',
    slate: 'bg-[#374151]/10 text-[#374151]',
  };

  return (
    <Card className="bg-white border border-[#D9E2F0] rounded-xl shadow-sm hover:shadow-lg hover:border-[#C7D5E8] transition-all h-full">
      <CardContent className="p-6 flex flex-col items-center text-center h-full">
        <div className={`inline-block self-start px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4 w-fit ${colorMap[tagColor]}`}>
          {tag}
        </div>
        <h3 className="text-xl font-bold text-[#0A1628] mb-4 w-full text-left">{name}</h3>
        
        <div className="space-y-3 flex-1 w-full">
          <div className="text-left">
            <span className="inline-flex items-center rounded-full border border-[#F3D7D2] bg-[#FFF4F2] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#B45309]">
              Problem
            </span>
            <p className="text-[#374151] text-sm mt-1">{problem}</p>
          </div>
          <div className="text-left">
            <span className="inline-flex items-center rounded-full border border-[#D6E4FF] bg-[#EFF6FF] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1A56DB]">
              Solution
            </span>
            <p className="text-[#374151] text-sm mt-1">{solution}</p>
          </div>
        </div>
        
        {external ? (
          <a 
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 text-[#1A56DB] font-semibold text-sm hover:underline self-start"
          >
            Learn More
            <ExternalLink size={14} />
          </a>
        ) : (
          <Link 
            to={link}
            className="mt-6 inline-flex items-center gap-2 text-[#1A56DB] font-semibold text-sm hover:underline self-start"
          >
            Learn More
            <ArrowRight size={14} />
          </Link>
        )}
      </CardContent>
    </Card>
  );
};

// Architecture tier component
const ArchitectureTier = ({ color, colorClass, label, title, description, capabilities, icon: Icon }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div 
      className={`bg-[#0F1D33] border border-white/8 rounded-lg p-6 cursor-pointer transition-all duration-300 hover:bg-[#12243D] ${colorClass}`}
      onClick={() => setIsExpanded(!isExpanded)}
    >
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-lg`} style={{ backgroundColor: `${color}20` }}>
          <Icon size={24} style={{ color }} />
        </div>
        <div className="flex-1">
          <span className="text-xs uppercase tracking-wider font-semibold" style={{ color }}>
            {label}
          </span>
          <h3 className="text-xl font-bold text-white mt-1">{title}</h3>
          <p className="text-white/65 text-sm mt-2">{description}</p>
          
          {isExpanded && (
            <ul className="mt-4 space-y-2 animate-fade-in">
              {capabilities.map((cap, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm text-white/80">
                  <ChevronRight size={14} style={{ color }} />
                  {cap}
                </li>
              ))}
            </ul>
          )}
          
          <button className="mt-3 text-sm font-medium flex items-center gap-1" style={{ color }}>
            {isExpanded ? 'Show less' : 'View capabilities'}
            <ChevronRight size={14} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          </button>
        </div>
      </div>
    </div>
  );
};

const Home = () => {
  const solutions = [
    {
      tag: 'Intelligence Foundation',
      tagColor: 'teal',
      name: 'Financial Data Intelligence',
      problem: 'Raw financial data — AA feeds to scanned ITRs — is unusable in credit models.',
      solution: '100+ credit-ready features from any financial data source.',
      link: '/solutions/financial-data-intelligence',
      external: false
    },
    {
      tag: 'Operations Layer',
      tagColor: 'purple',
      name: 'Ops Intelligence Suite',
      problem: 'Six critical workflows still run on manual handoffs.',
      solution: 'Six AI agents + configurable Business Rules Engine + human oversight.',
      link: '/solutions/ops-intelligence-suite',
      external: false
    },
    {
      tag: 'Execution Layer',
      tagColor: 'blue',
      name: 'Agentic Co-workers',
      problem: 'Your best underwriters spend their day reading bank statements.',
      solution: 'A co-worker that reads every file and drafts every CAM before you open it.',
      link: '/solutions/agentic-coworkers',
      external: false
    },
    {
      tag: 'Consumer & SME Layer',
      tagColor: 'amber',
      name: 'Financial Intelligence Assistant',
      problem: 'Consumers and SMEs have no intelligent financial layer to support better financial decisions.',
      solution: 'A personalised AI co-pilot — through your channels.',
      link: '/solutions/financial-intelligence-assistant',
      external: false
    },
    {
      tag: 'Model Risk Management',
      tagColor: 'slate',
      name: 'Aureon (MRM) - Cognifin Platform',
      problem: 'MRM governance runs on spreadsheets and fails audits too often.',
      solution: 'End-to-end lifecycle governance and one-click examiner packs.',
      link: '/solutions/aureon',
      external: false
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section - Animated Brand Ambient */}
      <section 
        className="hero-ambient relative overflow-hidden pt-32 pb-24 lg:pb-32 px-6" 
        data-testid="hero-section"
      >
        <div className="hero-ambient-backdrop" aria-hidden="true">
          <div className="hero-ambient-grid" />
          <div className="hero-ambient-glow hero-ambient-glow-blue" />
          <div className="hero-ambient-glow hero-ambient-glow-teal" />
          <div className="hero-ambient-glow hero-ambient-glow-amber" />
          <div className="hero-ambient-beam hero-ambient-beam-left" />
          <div className="hero-ambient-beam hero-ambient-beam-right" />
          <div className="hero-ambient-nodes">
            <span className="hero-ambient-node hero-node-one" />
            <span className="hero-ambient-node hero-node-two" />
            <span className="hero-ambient-node hero-node-three" />
          </div>
        </div>
        
        <div className="max-w-[1200px] mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            {/* Eyebrow */}
            <p className="text-[#6B7280] text-sm uppercase tracking-wider font-medium mb-6 italic">
              The Financial Intelligence Platform. Human at the Centre.
            </p>
            
            {/* Headline */}
            <h1 className="text-h1 hero-title mb-6" data-testid="hero-title">
              <span className="hero-title-line hero-title-line-primary text-white">
                <span className="hero-title-prefix">Banking&apos;s Next Decade Will Be Built on</span>
                <span className="hero-title-keyword">Four New Layers</span>
              </span>
              <span className="hero-title-line hero-title-line-secondary hero-title-supporting">
                We&apos;re Building Them!
              </span>
            </h1>
            
            {/* Body paragraph */}
            <p className="text-body-lg text-[#6B7280] mb-8 max-w-3xl mx-auto">
              Cognifin builds the intelligence infrastructure for financial institutions — converting fragmented data into structured intelligence, deploying agentic co-workers across credit, risk, and operations, and keeping human judgment at the centre of every decision.
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                size="lg" 
                className="bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-semibold text-lg px-8 h-14 btn-hover-lift shadow-lg shadow-[#1A56DB]/30"
                data-testid="request-demo-btn"
              >
                <Link to="/contact">Request Demo</Link>
              </Button>
              <Button 
                asChild 
                size="lg" 
                variant="outline" 
                className="border-2 border-white/30 text-white hover:bg-white hover:text-[#0A1628] text-lg px-8 h-14 font-semibold btn-hover-lift"
                data-testid="download-overview-btn"
              >
                <Link to="/use-cases">Explore Use Cases</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Ice Blue Background */}
      <section className="py-16 px-6 bg-[#EFF6FF]" data-testid="stats-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <AnimatedCounter end={80} suffix="%" />
              <p className="stat-label">TAT Reduction</p>
            </div>
            
            <div className="text-center">
              <span className="stat-number">&lt;15 mins</span>
              <p className="stat-label">CAM Generation</p>
            </div>
            
            <div className="text-center">
              <AnimatedCounter end={99} suffix="%" />
              <p className="stat-label">Audit Traceability</p>
            </div>
            
            <div className="text-center">
              <AnimatedCounter end={94} suffix="+" />
              <p className="stat-label">Credit Ready Features</p>
            </div>
          </div>
        </div>
      </section>

      {/* Four Convictions Section - Dark Navy */}
      <section className="section-padding px-6 bg-[#0A1628]" data-testid="convictions-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-h2 text-white mb-4">
              Four Convictions. Built Into Every Layer.
            </h2>
            <p className="text-body-lg text-[#6B7280]">
              Not features. Foundations.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <ConvictionTile
              icon={Users}
              title="Human at the Centre"
              body="Every AI action is reviewable. Every decision is a human decision. The machine prepares; the person decides."
            />
            
            <ConvictionTile
              icon={Settings}
              title="Your Rules, Your Way"
              body="Credit policies and compliance mandates configurable in real time. No engineering ticket required."
            />
            
            <ConvictionTile
              icon={Eye}
              title="Every Decision is Traceable"
              body="Full audit trail from raw data input to final decision output. Built for regulators, not just for teams."
            />
            
            <ConvictionTile
              icon={Scale}
              title="Intelligence Serves Judgment"
              body="AI that surfaces, structures, and synthesises — so your experts spend time deciding, not data gathering."
            />
          </div>
        </div>
      </section>

      {/* Solutions Preview Section - Ice Blue */}
      <section className="section-padding px-6 bg-[#EFF6FF]" data-testid="solutions-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-h2 text-[#0A1628] mb-4">
              Five Vertical Solutions. One Horizontal Architecture.
            </h2>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              One platform. Five entry points. Each solution is fully deployable — and all share the same four-tier intelligence stack beneath.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((solution, index) => (
              <SolutionCard
                key={index}
                tag={solution.tag}
                tagColor={solution.tagColor}
                name={solution.name}
                problem={solution.problem}
                solution={solution.solution}
                link={solution.link}
                external={solution.external}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Architecture Section - Dark Navy */}
      <section className="section-padding px-6 bg-[#0A1628]" data-testid="architecture-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-h2 text-white mb-4">
              Four-Tier Intelligence Architecture
            </h2>
            <p className="text-body-lg text-[#6B7280] max-w-3xl mx-auto">
              A layered approach to financial intelligence—from foundational data transformation to human-in-the-loop decision support
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <ArchitectureTier
              color="#0E7490"
              colorClass="tier-accent-teal"
              label="Tier 1"
              title="Intelligence Foundation"
              description="Core data transformation and feature engineering for financial intelligence"
              icon={Database}
              capabilities={[
                'Transaction Categorization Engine',
                'Entity Extraction & Resolution',
                'Feature Engineering Pipeline',
                'Document Intelligence (OCR, Parsing)',
                '100+ Credit-Ready Features'
              ]}
            />
            
            <ArchitectureTier
              color="#4F46E5"
              colorClass="tier-accent-purple"
              label="Tier 2"
              title="Business Rules & Policy"
              description="Configurable policy engine for compliance and risk management"
              icon={SlidersHorizontal}
              capabilities={[
                'Policy Compliance Checks',
                'Risk Signal Detection',
                'Regulatory Rule Engine',
                'Threshold Management',
                'Audit Trail Generation'
              ]}
            />
            
            <ArchitectureTier
              color="#1A56DB"
              colorClass="tier-accent-blue"
              label="Tier 3"
              title="Agentic Co-workers"
              description="AI agents that automate complex credit workflows end-to-end"
              icon={Bot}
              capabilities={[
                'Intake Automation Agent',
                'Financial Analysis Agent',
                'Underwriting Co-pilot',
                'CAM Generation Agent',
                'Multi-Agent Orchestration'
              ]}
            />
            
            <ArchitectureTier
              color="#B45309"
              colorClass="tier-accent-amber"
              label="Tier 4"
              title="Human Decision Layer"
              description="Explainable AI with human oversight for high-stakes decisions"
              icon={UserCheck}
              capabilities={[
                'Approval Gates',
                'Override Controls',
                'Explainable Recommendations',
                'Audit & Compliance Dashboard',
                'Decision Rationale Logging'
              ]}
            />
          </div>
        </div>
      </section>

      {/* Agent Log Simulation Section */}
      <section className="section-padding px-6 bg-[#EFF6FF]" data-testid="agent-simulation-section">
        <div className="max-w-[1200px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-h2 text-[#0A1628] mb-4">
                See AI Agents in Action
              </h2>
              <p className="text-body-lg text-[#6B7280] mb-6">
                Watch how our intelligent agents coordinate to process a loan application—from document intake to credit memo generation in under 15 minutes.
              </p>
              <Button 
                asChild 
                className="bg-[#1A56DB] hover:bg-[#1A56DB]/90 text-white font-semibold btn-hover-lift"
              >
                <Link to="/platform">Explore Platform</Link>
              </Button>
            </div>
            
            <div className="agent-log border border-white/8 shadow-2xl">
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-white/10">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="ml-2 text-[#6B7280] text-xs">cognifin-agent-orchestrator</span>
              </div>
              <div className="space-y-2">
                <p className="log-line">[Intake Agent] <span className="log-muted">Processing new loan application...</span></p>
                <p className="log-line">[Document Agent] <span className="log-muted">Extracting data from 12 documents...</span></p>
                <p className="log-line">[Document Agent] <span className="log-blue">✓ Bank statements parsed (24 months)</span></p>
                <p className="log-line">[Document Agent] <span className="log-blue">✓ GST returns validated</span></p>
                <p className="log-line">[Analysis Agent] <span className="log-muted">Generating cashflow analysis...</span></p>
                <p className="log-line">[Analysis Agent] <span className="log-blue">✓ 87 risk signals computed</span></p>
                <p className="log-line">[Policy Agent] <span className="log-muted">Checking compliance rules...</span></p>
                <p className="log-line">[Policy Agent] <span className="log-blue">✓ All policy checks passed</span></p>
                <p className="log-line">[CAM Agent] <span className="log-muted">Drafting credit assessment memo...</span></p>
                <p className="log-line">[Orchestrator] <span className="text-green-400 font-semibold">Ready for human review</span><span className="cursor"></span></p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Dark Navy Gradient */}
      <section className="section-padding px-6 bg-gradient-to-r from-[#0A1628] to-[#1A56DB]" data-testid="cta-section">
        <div className="max-w-[1200px] mx-auto text-center">
          <h2 className="text-h2 text-white mb-6">
            Ready to Transform Your Operations?
          </h2>
          <p className="text-body-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Join leading BFSI enterprises leveraging AI-native intelligence for faster, smarter credit decisions
          </p>
          <Button 
            asChild 
            size="lg" 
            className="bg-white hover:bg-white/90 text-[#0A1628] font-bold text-lg px-10 h-14 btn-hover-lift shadow-xl"
            data-testid="schedule-demo-btn"
          >
            <Link to="/contact">Schedule a Demo</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;
